import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Shared hooks extracted from MessagingThread for reuse in ConversationThread.
 * These handle iOS-specific keyboard, scroll, viewport, and back-button behavior.
 */

/** Modal back-button handler (prevents navigation when modal is open) */
export const useModalBackHandler = (isOpen, onClose) => {
  const pushedStateRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const mountedRef = useRef(false);

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    // Track if this is a real mount (not Strict Mode double-render)
    const wasAlreadyMounted = mountedRef.current;
    mountedRef.current = true;
    
    if (!isOpen) { pushedStateRef.current = false; return; }

    if (!pushedStateRef.current) {
      window.history.pushState({ messagingModal: true, timestamp: Date.now() }, '');
      pushedStateRef.current = true;
    }

    const handlePopState = (event) => {
      if (pushedStateRef.current) {
        event.preventDefault();
        pushedStateRef.current = false;
        onCloseRef.current();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Only call history.back() if this is a real unmount, not Strict Mode cleanup
      // In Strict Mode, the component will immediately remount, so we shouldn't navigate
      // We use a small timeout to check if the component remounts
      if (pushedStateRef.current && window.history.state?.messagingModal) {
        const currentPushedState = pushedStateRef.current;
        pushedStateRef.current = false;
        // Delay the history.back() to allow Strict Mode remount to happen first
        setTimeout(() => {
          // If the component didn't remount (mountedRef is still false after unmount),
          // then this is a real unmount and we should go back
          if (!mountedRef.current && currentPushedState) {
            window.history.back();
          }
        }, 50);
      }
      mountedRef.current = false;
    };
  }, [isOpen]);
};

/** iOS keyboard inset tracking.
 *
 * On Capacitor native iOS, `window.visualViewport` does NOT shrink when the
 * keyboard appears (because `Keyboard.resize` is set to `"none"` in
 * capacitor.config.json), so `innerHeight - visualViewport.height` stays 0
 * and the composer ends up behind the keyboard.
 *
 * On native: subscribe to the Capacitor `Keyboard` plugin's
 * `keyboardWillShow` / `keyboardWillHide` events.
 * On web: fall back to `visualViewport` resize/scroll.
 */
export const useKeyboardInset = (isOpen) => {
  const [keyboardInset, setKeyboardInset] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setKeyboardInset(0);
      return;
    }

    let cleanup = () => {};
    let cancelled = false;

    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
          const { Keyboard } = await import('@capacitor/keyboard');
          if (cancelled) return;

          const showHandle = await Keyboard.addListener('keyboardWillShow', (info) => {
            setKeyboardInset((info && typeof info.keyboardHeight === 'number') ? info.keyboardHeight : 0);
          });
          const hideHandle = await Keyboard.addListener('keyboardWillHide', () => {
            setKeyboardInset(0);
          });
          cleanup = () => {
            try { showHandle.remove && showHandle.remove(); } catch {}
            try { hideHandle.remove && hideHandle.remove(); } catch {}
            setKeyboardInset(0);
          };
          return;
        }
      } catch {
        // Capacitor not available — fall through to web fallback.
      }

      // Web fallback: visualViewport (works on iOS Safari and modern browsers).
      const update = () => {
        if (window.visualViewport) {
          setKeyboardInset(Math.max(0, window.innerHeight - window.visualViewport.height));
        }
      };
      update();
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', update);
        window.visualViewport.addEventListener('scroll', update);
      }
      cleanup = () => {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', update);
          window.visualViewport.removeEventListener('scroll', update);
        }
        setKeyboardInset(0);
      };
    })();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [isOpen]);

  return keyboardInset;
};

/** Body scroll lock for modal (critical for iOS Chrome) */
export const useBodyScrollLock = (isOpen) => {
  const savedScrollY = useRef(0);

  useEffect(() => {
    if (isOpen) {
      savedScrollY.current = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScrollY.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, savedScrollY.current);
      };
    }
  }, [isOpen]);
};

/** iOS viewport zoom reset */
export const useResetViewportZoom = () => {
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  return useCallback(() => {
    if (!isIOS) return;
    const currentScale = window.visualViewport?.scale || 1;
    if (currentScale <= 1.01) return;
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    const original = meta.getAttribute('content');
    meta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
    requestAnimationFrame(() => {
      setTimeout(() => {
        meta.setAttribute('content', original || 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
      }, 150);
    });
  }, [isIOS]);
};

/** Landscape orientation detection */
export const useLandscapeDetect = () => {
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(orientation: landscape)');
    setIsLandscape(mq.matches);
    const handler = (e) => setIsLandscape(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isLandscape;
};

/** Force scroll to bottom with multi-pass (iOS Chrome reliability) */
export const useForceScrollToBottom = (scrollRef) => {
  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    });
  }, [scrollRef]);

  const forceScrollToBottom = useCallback(() => {
    const scroll = () => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; };
    scroll();
    requestAnimationFrame(() => { scroll(); requestAnimationFrame(scroll); });
  }, [scrollRef]);

  return { scrollToBottom, forceScrollToBottom };
};
